import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// Tolerance for floating-point rounding differences
const VALIDATION_TOLERANCE = 0.05;

export interface POLineItem {
  id: string;
  purchase_order_id: string;
  file_id: string | null;
  sku: string | null;
  description: string | null;
  ean_code: string | null;
  quantity: number;
  uom: string | null;
  unit_price: number;
  discount_percent: number;
  discount_amount: number;
  line_total: number;
  expected_gmv: number;
  expected_nmv: number;
  advance_rate: number;
  net_expected_realizable_value: number;
  suggested_purchase_price: number;
  capped_purchase_price: number;
  confidence_score: number;
  needs_review: boolean;
  review_notes: string | null;
  extraction_source: (Record<string, unknown> & {
    sku_report_breakdown?: {
      total_gmv: number;
      total_nmv: number;
      units_sold: number;
      avg_gmv: number;
      avg_nmv: number;
      stock_held: number;
      dr_percent: number;
      velocity: number;
    };
  }) | null;
  verified_by_email: string | null;
  verified_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface LineItemValidation {
  isValid: boolean;
  expectedTotal: number;
  difference: number;
}

export function validateLineItem(item: POLineItem): LineItemValidation {
  const expectedTotal = (item.quantity * item.unit_price) - item.discount_amount;
  const difference = Math.abs(expectedTotal - item.line_total);
  return {
    isValid: difference <= VALIDATION_TOLERANCE,
    expectedTotal: Math.round(expectedTotal * 100) / 100,
    difference: Math.round(difference * 100) / 100,
  };
}

export interface POLineItemUpdate {
  sku?: string;
  description?: string;
  quantity?: number;
  unit_price?: number;
  discount_percent?: number;
  discount_amount?: number;
  line_total?: number;
  needs_review?: boolean;
  review_notes?: string;
  verified_by_email?: string;
  verified_at?: string;
}

export function usePOLineItems(purchaseOrderId: string | null) {
  return useQuery({
    queryKey: ["po-line-items", purchaseOrderId],
    queryFn: async () => {
      if (!purchaseOrderId) return [];
      
      const { data, error } = await supabase
        .from("purchase_order_line_items")
        .select("*")
        .eq("purchase_order_id", purchaseOrderId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      // Sort by line_number from extraction_source to preserve original PDF order
      const items = data as POLineItem[];
      return items.sort((a, b) => {
        const aNum = (a.extraction_source as Record<string, unknown>)?.line_number as number | undefined;
        const bNum = (b.extraction_source as Record<string, unknown>)?.line_number as number | undefined;
        if (aNum != null && bNum != null) return aNum - bNum;
        if (aNum != null) return -1;
        if (bNum != null) return 1;
        return 0;
      });
    },
    enabled: !!purchaseOrderId,
  });
}

export function usePOLineItemsSummary(purchaseOrderId: string | null) {
  const { data: lineItems, isLoading, error } = usePOLineItems(purchaseOrderId);

  const summary = lineItems ? {
    totalItems: lineItems.length,
    itemsNeedingReview: lineItems.filter(item => item.needs_review).length,
    itemsVerified: lineItems.filter(item => item.verified_at).length,
    totalGEV: lineItems.reduce((sum, item) => sum + item.expected_gmv, 0),
    totalNEV: lineItems.reduce((sum, item) => sum + item.expected_nmv, 0),
    totalNERV: lineItems.reduce((sum, item) => sum + item.net_expected_realizable_value, 0),
    totalSuggestedPrice: lineItems.reduce((sum, item) => sum + item.suggested_purchase_price, 0),
    totalCappedPrice: lineItems.reduce((sum, item) => sum + item.capped_purchase_price, 0),
    advanceRate: lineItems[0]?.advance_rate || 0.8,
    totalLineTotal: lineItems.reduce((sum, item) => sum + item.line_total, 0),
    itemsWithMathErrors: lineItems.filter(item => !validateLineItem(item).isValid).length,
  } : null;

  return { summary, lineItems, isLoading, error };
}

export function useUpdatePOLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: POLineItemUpdate }) => {
      // Recalculate funding if quantity, price, or discount changed
      let recalculatedFields: Partial<POLineItem> = {};
      
      if (updates.quantity !== undefined || updates.unit_price !== undefined || updates.discount_amount !== undefined) {
        const { data: currentItem } = await supabase
          .from("purchase_order_line_items")
          .select("quantity, unit_price, discount_amount, advance_rate")
          .eq("id", id)
          .single();

        if (currentItem) {
          const quantity = updates.quantity ?? currentItem.quantity;
          const unitPrice = updates.unit_price ?? currentItem.unit_price;
          const discountAmount = updates.discount_amount ?? currentItem.discount_amount;

          // Only recalculate line_total client-side (PO math).
          // GEV/NEV depend on historical data and cannot be recalculated here.
          const lineTotal = (quantity * unitPrice) - discountAmount;

          recalculatedFields = {
            line_total: updates.line_total ?? Math.round(lineTotal * 100) / 100,
          };
        }
      }

      const { data, error } = await supabase
        .from("purchase_order_line_items")
        .update({
          ...updates,
          ...recalculatedFields,
          updated_at: new Date().toISOString(),
        } as any)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["po-line-items", data.purchase_order_id] });
    },
  });
}

export function useVerifyPOLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, email }: { id: string; email: string }) => {
      const { data, error } = await supabase
        .from("purchase_order_line_items")
        .update({
          needs_review: false,
          verified_by_email: email,
          verified_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["po-line-items", data.purchase_order_id] });
    },
  });
}

export function useBulkVerifyPOLineItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ids, email, purchaseOrderId }: { ids: string[]; email: string; purchaseOrderId: string }) => {
      const { error } = await supabase
        .from("purchase_order_line_items")
        .update({
          needs_review: false,
          verified_by_email: email,
          verified_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .in("id", ids);

      if (error) throw error;
      return { purchaseOrderId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["po-line-items", data.purchaseOrderId] });
    },
  });
}
